// @ts-nocheck
import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import InstructionFormDialog from './InstructionFormDialog';
import HeadingInfo from './HeadingInfo';
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';

const { USER_INSTRUCTION_MAX_ITEMS, USER_INSTRUCTION_MAX } = AI_AGENT_FIELD_LIMITS;
const MAX_CHARS = USER_INSTRUCTION_MAX_ITEMS * USER_INSTRUCTION_MAX;

export default function InstructionsCard({ instructions, onChange }: any) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const totalChars = instructions.reduce((sum: number, i: any) => sum + (i.body?.length || 0), 0);

  const openNew = () => {
    if (instructions.length >= USER_INSTRUCTION_MAX_ITEMS) {
      toast.error(`You can add up to ${USER_INSTRUCTION_MAX_ITEMS} instructions`);
      return;
    }
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (item: any) => {
    setEditing(item);
    setDialogOpen(true);
  };

  const handleSave = (item: any) => {
    if (item.id) {
      onChange(instructions.map((i: any) => (i.id === item.id ? { ...i, ...item } : i)));
    } else {
      onChange([...instructions, { ...item, id: crypto.randomUUID(), enabled: true }]);
    }
  };

 const toggleEnabled = (id: string) => {
  const currentInstruction = instructions.find((i: any) => i.id === id);

  if (currentInstruction?.enabled !== false) {
    const selectedCount = instructions.filter(
      (i: any) => i.enabled !== false,
    ).length;

    if (selectedCount <= 1) {
      toast.error('At least one instruction must be selected');
      return;
    }
  }

  onChange(
    instructions.map((i: any) =>
      i.id === id ? { ...i, enabled: !i.enabled } : i,
    ),
  );
};

  const remove = (id: string) => {
    onChange(instructions.filter((i: any) => i.id !== id));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-1.5 text-base">
          Instructions<span className="text-destructive">*</span>
          <HeadingInfo text="Extra task-specific instructions on top of the system prompt. Example: “Answer order status questions using the order lookup API.”" />
          <span className="text-xs font-normal text-muted-foreground">
            ({instructions.length}/{USER_INSTRUCTION_MAX_ITEMS} instructions, {totalChars}/{MAX_CHARS} characters)
          </span>
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={openNew}
          disabled={instructions.length >= USER_INSTRUCTION_MAX_ITEMS}
        >
          <Plus className="h-4 w-4" />
          Instructions
        </Button>
      </CardHeader>
      <CardContent>
        {instructions.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No instructions added yet.</p>
        ) : (
          <div className="space-y-2">
            {instructions.map((item: any) => (
              <div
                key={item.id}
                className="flex items-start gap-3 rounded-md border border-border p-3"
              >
                <Checkbox
                  checked={item.enabled !== false}
                  onCheckedChange={() => toggleEnabled(item.id)}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{item.title}</span>
                    <span className="text-xs text-muted-foreground">
                      ({item.body?.length || 0} character used)
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{item.body}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => remove(item.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <InstructionFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSave={handleSave}
      />
    </Card>
  );
}
